import { FunctionComponent, useEffect, useState } from 'react'
import styled from 'styled-components'
import Text from '../../../components/Text'
import { useAuthStore } from '../../../stores/auth/useAuthStore'
import { useLocation } from 'react-router-dom'
import { AppRoutes } from '../../../interfaces/store/appStore'
import posthog from 'posthog-js'
import { BillingStoreProvider } from '../../../stores/billing/useBillingStore'
import { useBillingHandlers } from '../../../services/actions/billing/useBillingHandlers'
import { useAppStore } from '../../../stores/application/useAppStore'
import PlansAndPricingSection from '../../billing/sections/PlansAndPricingSection'
import InitialLoading from '../../../components/InitialLoading'

interface IDisabledSectionProps { }

const ExpiredBillingContent: FunctionComponent = () => {
    const [billingPlansLoading, setBillingPlansLoading] = useState<boolean>(true)
    const { getBillingPlansHandler, getPlansFeaturesHandler } = useBillingHandlers()
    const { store: { requestLoading } } = useAppStore()

    useEffect(() => {
        getBillingPlansHandler()
        getPlansFeaturesHandler()
    }, [])

    useEffect(() => {
        if ('billingPlans' in requestLoading) setBillingPlansLoading(requestLoading?.['billingPlans'])
    }, [requestLoading])

    if (billingPlansLoading) {
        return <InitialLoading showInitialLoading={true} />
    }

    return (
        <BillingEmbed>
            <PlansAndPricingSection hideTitle />
        </BillingEmbed>
    )
}

const DisabledSection: FunctionComponent<IDisabledSectionProps> = () => {
    const { store: { workspaceOwner, workspaceAdmin, subscription } } = useAuthStore()
    const location = useLocation()
    const canManageBilling = workspaceOwner || workspaceAdmin
    const isPaused = !!(subscription?.pausedByAdmin || subscription?.pausedUntil)

    useEffect(() => {
        const params = new URLSearchParams(location.search || '')
        const featureFromQuery = params.get('feature_name') || params.get('feature') || undefined

        const stateFrom = (location.state as any)?.from as string | undefined
        const referrerPath =
            stateFrom ||
            sessionStorage.getItem('ld_prev_path') ||
            (typeof document !== 'undefined' && document.referrer ? (() => {
                try { return new URL(document.referrer).pathname } catch { return '' }
            })() : '') ||
            ''

        const subscriptionStatus =
            subscription?.status ||
            (subscription?.pausedByAdmin || subscription?.pausedUntil ? 'paused' : undefined) ||
            'unknown'

        // Fallback feature name heuristic based on where they came from
        const featureName =
            featureFromQuery ||
            (referrerPath.startsWith(AppRoutes.BILLING) ? 'billing' : undefined) ||
            (referrerPath.startsWith(AppRoutes.DASHBOARD) ? 'dashboard' : undefined) ||
            (referrerPath.startsWith(AppRoutes.TAGS) ? 'tags' : undefined) ||
            (referrerPath.startsWith(AppRoutes.TASKS) ? 'tasks' : undefined) ||
            (referrerPath.startsWith(AppRoutes.TEMPLATES) ? 'templates' : undefined) ||
            (referrerPath.startsWith(AppRoutes.NOTES) ? 'notes' : undefined) ||
            (referrerPath.startsWith(AppRoutes.POSTS) ? 'posts' : undefined) ||
            (referrerPath.startsWith(AppRoutes.CSV_ENRICHMENT) ? 'csvEnrichment' : undefined) ||
            (referrerPath.startsWith(AppRoutes.PROSPECTS) ? 'prospects' : undefined) ||
            (referrerPath.startsWith(AppRoutes.AI_CHAT) ? 'aiChat' : undefined) ||
            (referrerPath.startsWith(AppRoutes.INTEGRATIONS) ? 'integrations' : undefined) ||
            (referrerPath.startsWith(AppRoutes.SETTINGS) ? 'settings' : undefined) ||
            (referrerPath.startsWith(AppRoutes.SETTINGS_DATA_MANAGEMENT) ? 'dataManagement' : undefined) ||
            (referrerPath.startsWith(AppRoutes.SETTINGS_EXTENSION) ? 'extension' : undefined) ||
            (referrerPath.startsWith(AppRoutes.SETTINGS_LINKEDIN) ? 'linkedin' : undefined) ||
            (referrerPath.startsWith(AppRoutes.SETTINGS_BILLING) ? 'billing' : undefined) ||
            (referrerPath.startsWith(AppRoutes.CONNECTIONS) ? 'connections' : undefined) ||
            (referrerPath.startsWith(AppRoutes.TAGS) ? 'tags' : undefined) ||
            (referrerPath.startsWith(AppRoutes.SIGNUP) ? 'signup' : undefined) ||
            (referrerPath.startsWith(AppRoutes.SIGNIN) ? 'signin' : undefined) ||
            (referrerPath.startsWith(AppRoutes.FORGOT_PASSWORD) ? 'forgotPassword' : undefined) ||
            (referrerPath.startsWith(AppRoutes.DELETE_USER_CONFIRMATION) ? 'deleteUserConfirmation' : undefined) ||
            (referrerPath.startsWith(AppRoutes.ONBOARDING) ? 'onboarding' : undefined) ||
            (referrerPath.startsWith(AppRoutes.ONBOARDING_SURVEY) ? 'onboardingSurvey' : undefined) ||
            (referrerPath.startsWith(AppRoutes.ONBOARDING_EXTENSION_INSTALL) ? 'onboardingExtensionInstall' : undefined) ||
            (referrerPath.startsWith(AppRoutes.ONBOARDING_LINKEDIN) ? 'onboardingLinkedIn' : undefined) ||
            (referrerPath.startsWith(AppRoutes.WORKSPACE_MANAGEMENT) ? 'workspaceManagement' : undefined) ||
            (referrerPath.startsWith(AppRoutes.CREATE_WORKSPACE) ? 'createWorkspace' : undefined) ||
            (referrerPath.startsWith(AppRoutes.INFO) ? 'info' : undefined) ||
            (referrerPath.startsWith(AppRoutes.REDIRECT) ? 'redirect' : undefined) ||
            (referrerPath.startsWith(AppRoutes.INVITE) ? 'invite' : undefined) ||
            (referrerPath.startsWith(AppRoutes.SSO_CALLBACK) ? 'ssoCallback' : undefined) ||
            (referrerPath.startsWith(AppRoutes.CSV_IMPORT) ? 'csvImport' : undefined) ||
            (referrerPath.startsWith(AppRoutes.CSV_MAPPING) ? 'csvMapping' : undefined) ||
            (referrerPath.startsWith(AppRoutes.HUBSPOT_MAPPING) ? 'hubspotMapping' : undefined) ||
            (referrerPath.startsWith(AppRoutes.HOME) ? 'home' : undefined) ||
            'unknown'

        const payload = {
            feature_name: featureName,
            referrer_path: referrerPath,
            subscription_status: subscriptionStatus,
        }

        posthog.capture('disabled page viewed', payload)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return <Container $embedBilling={!isPaused && canManageBilling}>
        {isPaused ? (
            <>
                <Spacing />
                <Text $heading3>Your LeadDelta subscription has been paused</Text>
                <TextCenter>
                    <Text $paragraph1 $bold>Your subscription is paused, and you don’t have access to this workspace anymore.</Text>
                    <Text $paragraph1 $bold>Contact your workspace admin to reactivate the subscription.</Text>
                </TextCenter>
                <VideoContainer>
                    <iframe
                                src="https://www.loom.com/embed/0e5cd4bec17a45dab4fede942c4463cb?sid=f802cd83-323d-409b-a2e4-ce9087d0d4ec"
                                frameBorder="0"
                                allowFullScreen
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%'
                                }}
                        />
                </VideoContainer>
            </>
        ) : (
            <>
                <ExpiredHeader>
                    <Text $heading3>Your LeadDelta has expired</Text>
                    <Text $paragraph1>
                        {canManageBilling
                            ? 'Select your plan & billing cycle and upgrade'
                            : 'Contact your workspace admin to purchase'}
                    </Text>
                </ExpiredHeader>
                {canManageBilling && (
                    <BillingStoreProvider>
                        <ExpiredBillingContent />
                    </BillingStoreProvider>
                )}
            </>
        )}
    </Container>
}

const Container = styled.div<{ $embedBilling?: boolean }>`
    width: 100%;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: ${({ $embedBilling }) => $embedBilling ? '16px' : '20px'};
    height: 100%;
    overflow-y: auto;
    align-items: center;
    background: ${({ theme: { secondaryColor }}) => secondaryColor};
`
const ExpiredHeader = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    text-align: center;
    padding-top: 16px;
`
const BillingEmbed = styled.div`
    width: 100%;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
`
const VideoContainer = styled.div`
    position: relative;
    width: 60%;
    overflow: hidden;
    border-radius: 4px;
    flex-grow: 1;
    flex: 1 1;
    min-width: 400px;
`
const TextCenter = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 12px;
`
const Spacing = styled.div`
    width: 100%;
    height: 32px;
`

export default DisabledSection
